import React, { useEffect } from 'react';
import './style.css';
import FormRadioButton from '../../baseComponents/RadioButton';
import FormRadioGroup from '../../baseComponents/Radiogroup';
import systemTheme from '../../../assets/systemTheme.svg';
import lightTheme from '../../../assets/lightTheme.svg';
import darkTheme from '../../../assets/darkTheme.svg';
import ColorPickerWithLabel from '../../baseComponents/ColorPickerWithLabel';
import SelectionWithLabel from '../../baseComponents/SelectionWithLabel';
import { generateExtendedColors } from '../../../sampleApp/src/utils/utils';
import type { FormInstance } from 'antd/es/form';
import { ColorPicker } from 'antd';
import { fontOptions, fontSizes, sizeOptions } from '../../../sampleApp/src/styleConfig';
import useSystemColorScheme from '../../../sampleApp/src/customHooks';
import { useCometChatContext } from '../../../sampleApp/src/context/CometChatContext';

/**
 * Props for the RightSideBar component.
 */
interface RightSideBarProps {
  /** The Ant Design form instance. */
  form: FormInstance;
}

/** Type definition for color picker value */
type ColorType = Parameters<NonNullable<React.ComponentProps<typeof ColorPicker>['onChange']>>[0];

/**
 * RightSideBar component provides theme, color, and typography customization options.
 *
 * @param {RightSideBarProps} props - Component props.
 * @returns {JSX.Element} The rendered RightSideBar component.
 */
const RightSideBar = ({ form }: RightSideBarProps) => {
  const { styleFeatures, setStyleFeatures } = useCometChatContext();

  const colorScheme = useSystemColorScheme();

  /** Updates extended colors whenever the theme changes */
  useEffect(() => {
    generateExtendedColors();
  }, [styleFeatures.theme]);

  /**
   * Handles color picker changes and updates the corresponding theme properties.
   *
   * @param {string} color - Selected color in hex format.
   * @param {string} key - The key representing the theme property.
   */
  const handleColorPickerChange = (color: string, key: string) => {
    const currentTheme = styleFeatures?.theme;

    if (!currentTheme) {
      console.warn('Theme not found:', currentTheme);
      return;
    }

    const themeElement = document.getElementById(`${currentTheme}-theme`);
    const colorValue = `#${color}`;

    const updateThemeColor = (property: string) => {
      document.documentElement.style.setProperty(property, colorValue);
      if (themeElement) {
        themeElement.style.setProperty(property, colorValue);
      }
    };

    if (key === 'brandColor') {
      updateThemeColor('--cometchat-primary-color');
      generateExtendedColors();
    } else {
      const isPrimaryText = key === 'primaryTextLight' || key === 'primaryTextDark';
      const isSecondaryText = key === 'secondaryTextLight' || key === 'secondaryTextDark';

      const isMatchingColorScheme =
        (key.includes('Light') && colorScheme === 'light') ||
        (key.includes('Dark') && colorScheme === 'dark') ||
        (key.includes('Light') && styleFeatures.theme === 'light') ||
        (key.includes('Dark') && styleFeatures.theme === 'dark');

      if (isMatchingColorScheme) {
        if (isPrimaryText) updateThemeColor('--cometchat-text-color-primary');
        if (isSecondaryText) updateThemeColor('--cometchat-text-color-secondary');
      }
    }

    const updatedColors = {
      ...form.getFieldValue('style')?.color,
      [key]: colorValue,
    };

    form.setFieldsValue({
      style: {
        ...form.getFieldValue('style'),
        color: updatedColors,
      },
    });

    setStyleFeatures({
      ...form.getFieldValue('style'),
      color: updatedColors,
    });
  };

  /** Updates theme colors when the system theme changes */
  useEffect(() => {
    changeTheme(styleFeatures.theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorScheme]);

  /**
   * Changes the theme and updates text colors accordingly.
   *
   * @param {string} value - The selected theme ('light', 'dark', or 'system').
   */
  const changeTheme = (value: string) => {
    const currentTheme = styleFeatures?.theme;
    const themeElement = document.getElementById(`${currentTheme}-theme`);

    if (!themeElement) return;

    const primaryColor = getComputedStyle(themeElement).getPropertyValue('--cometchat-text-color-primary').trim();
    const secondaryColor = getComputedStyle(themeElement).getPropertyValue('--cometchat-text-color-secondary').trim();

    const updateThemeColors = (primary: string, secondary: string) => {
      if (primaryColor !== primary) {
        themeElement.style.setProperty('--cometchat-text-color-primary', primary);
      }
      if (secondaryColor !== secondary) {
        themeElement.style.setProperty('--cometchat-text-color-secondary', secondary);
      }
    };

    let primary = styleFeatures.color.primaryTextLight;
    let secondary = styleFeatures.color.secondaryTextLight;

    if (value === 'dark' || (value === 'system' && colorScheme === 'dark')) {
      primary = styleFeatures.color.primaryTextDark;
      secondary = styleFeatures.color.secondaryTextDark;
    }

    updateThemeColors(primary, secondary);

    setStyleFeatures((prevFeatures) => ({
      ...prevFeatures,
      theme: value,
    }));

    form.setFieldsValue({
      style: {
        ...form.getFieldValue('style'),
        theme: value,
      },
    });
  };

  /**
   * Handles font selection change.
   *
   * @param {string | number} value - The selected font.
   */
  const handleFontChange = (value: string | number) => {
    document.documentElement.style.setProperty('--cometchat-font-family', value as string);

    setStyleFeatures({
      ...form.getFieldValue('style'),
      typography: {
        ...form.getFieldValue('style')?.typography,
        font: value,
      },
    });
  };

  /**
   * Handles font size selection change.
   *
   * @param {string | number} value - The selected font size category.
   */
  const handleFontSizeChange = (value: string | number) => {
    const selectedFontSize = fontSizes[value as 'compact' | 'default' | 'comfortable'] || {};
    Object.entries(selectedFontSize).forEach(([key, val]) => {
      document.documentElement.style.setProperty(key, val as string);
    });

    setStyleFeatures({
      ...form.getFieldValue('style'),
      typography: {
        ...form.getFieldValue('style')?.typography,
        size: value,
      },
    });
  };

  let colorPickerViewForTextColor = <></>;

  if (styleFeatures.theme === 'system') {
    colorPickerViewForTextColor = (
      <>
        <ColorPickerWithLabel
          label={
            <div className="lightAndDarkTextWrapper">
              Primary Text <sup className="lightAndDarkText">Light</sup>
            </div>
          }
          name={['style', 'color', 'primaryTextLight']}
          value={styleFeatures?.color?.primaryTextLight}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'primaryTextLight')}
        />
        <ColorPickerWithLabel
          label={
            <div className="lightAndDarkTextWrapper">
              Secondary Text <sup className="lightAndDarkText">Light</sup>
            </div>
          }
          name={['style', 'color', 'secondaryTextLight']}
          value={styleFeatures?.color?.secondaryTextLight}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'secondaryTextLight')}
        />
        <ColorPickerWithLabel
          label={
            <div className="lightAndDarkTextWrapper">
              Primary Text <sup className="lightAndDarkText">Dark</sup>
            </div>
          }
          name={['style', 'color', 'primaryTextDark']}
          value={styleFeatures?.color?.primaryTextDark}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'primaryTextDark')}
        />

        <ColorPickerWithLabel
          label={
            <div className="lightAndDarkTextWrapper">
              Secondary Text <sup className="lightAndDarkText">Dark</sup>
            </div>
          }
          name={['style', 'color', 'secondaryTextDark']}
          value={styleFeatures?.color?.secondaryTextDark}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'secondaryTextDark')}
        />
      </>
    );
  }

  if (styleFeatures.theme === 'light') {
    colorPickerViewForTextColor = (
      <>
        <ColorPickerWithLabel
          label="Primary Text"
          name={['style', 'color', 'primaryTextLight']}
          value={styleFeatures?.color?.primaryTextLight}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'primaryTextLight')}
        />
        <ColorPickerWithLabel
          label="Secondary Text"
          name={['style', 'color', 'secondaryTextLight']}
          value={styleFeatures?.color?.secondaryTextLight}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'secondaryTextLight')}
        />
      </>
    );
  }

  if (styleFeatures.theme === 'dark') {
    colorPickerViewForTextColor = (
      <>
        <ColorPickerWithLabel
          label="Primary Text"
          name={['style', 'color', 'primaryTextDark']}
          value={styleFeatures?.color?.primaryTextDark}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'primaryTextDark')}
        />
        <ColorPickerWithLabel
          label="Secondary Text"
          name={['style', 'color', 'secondaryTextDark']}
          value={styleFeatures?.color?.secondaryTextDark}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'secondaryTextDark')}
        />
      </>
    );
  }

  return (
    <div id="rightSidebarWrapper" className="rightSidebarWrapper">
      <div className="featureHeader styleHeader">Style</div>
      <div className="themeHeader">Theme</div>
      <div className="themeContent">
        <FormRadioGroup
          key="themeSideBar"
          formItemStyles={{ marginBottom: '0px', width: '100%' }}
          name={['style', 'theme']}
        >
          <div className="themeRadioWrapper radioWrapper" id="radioWrapper">
            <FormRadioButton
              id="system"
              icon={<img src={systemTheme} alt="region US" />}
              label="System"
              value="system"
              onClick={() => changeTheme('system')}
            />
            <FormRadioButton
              id="light"
              icon={<img src={lightTheme} alt="light theme" />}
              label="Light"
              value="light"
              onClick={() => changeTheme('light')}
            />
            <FormRadioButton
              id="dark"
              icon={<img src={darkTheme} alt="dark theme" />}
              label="Dark"
              value="dark"
              onClick={() => changeTheme('dark')}
            />
          </div>
        </FormRadioGroup>
      </div>
      <div className="colorHeader">Colors</div>
      <div className="colorsWrapper">
        <ColorPickerWithLabel
          label="Brand Color"
          value={styleFeatures?.color?.brandColor}
          name={['style', 'color', 'brandColor']}
          onChange={(color: ColorType) => handleColorPickerChange(color.toHex(), 'brandColor')}
        />
        {colorPickerViewForTextColor}
      </div>
      <div className="typographyHeader">Typography</div>
      <div className="typoGraphyWrapper">
        <SelectionWithLabel
          label="Font"
          name={['style', 'typography', 'font']}
          options={fontOptions}
          placeholder="Roboto"
          onChange={handleFontChange}
        />
        <SelectionWithLabel
          label="Size"
          name={['style', 'typography', 'size']}
          options={sizeOptions}
          placeholder="Default"
          onChange={handleFontSizeChange}
        />
      </div>
    </div>
  );
};

export default RightSideBar;
